import useSheetHistory from '@/common/hooks/useSheetHistory';
import { DsDrawer } from '@/modules/doctorHome/components/DsDrawer';
import useResponsive from '@/common/hooks/useResponsive';
import classNames from '@/common/utils/classNames';
import dynamic from 'next/dynamic';
import { Drawer } from 'vaul';
import CloseIcon from '../../icons/close';
import ClientOnlyPortal from '../../layouts/clientOnlyPortal';
const Transition = dynamic(() => import('../transition'));

/** بالاتر از شیت‌های معمولی داشبورد (level 0) تا لاگین/پرداخت روی آن‌ها دیده شود */
const MODAL_LEVEL = 5;

export interface ModalProps {
  dismissible?: boolean;
  isOpen: boolean;
  onClose: (_?: any) => void;
  children: React.ReactNode;
  title?: React.ReactNode;
  fullScreen?: boolean;
  bodyClassName?: string;
  className?: string;
  noHeader?: boolean;
  noScroll?: boolean;
  noLine?: boolean;
  /** نام شیت در آدرس (`?sheets=<name>`) — دکمهٔ بازگشت مرورگر آن را می‌بندد */
  name?: string;
  bindHistory?: boolean;
  /** موبایل: شیتی که روی شیت دیگری باز می‌شود (Drawer.NestedRoot) تا کشیدنش شیت زیرین را نبندد */
  nested?: boolean;
}

/** دسکتاپ: مودال قبلی سایت، دست‌نخورده. */
const DesktopModal = (props: ModalProps) => {
  const { title, isOpen, onClose, children, fullScreen, bodyClassName, className, noHeader = false, noLine, noScroll = false } = props;

  return (
    <ClientOnlyPortal selector="body">
      <Transition
        match={isOpen}
        animation="fade"
        duration={180}
        className="fixed top-0 bottom-0 left-0 right-0 flex items-end md:pb-14 z-infinity pointer-events-auto overscroll-none md:pt-20 md:justify-center md:items-start bg-slate-900 bg-opacity-60"
        onClick={props.dismissible === false ? undefined : onClose}
        id="modal"
      >
        <Transition
          match={isOpen}
          animation={'bottom'}
          duration={240}
          delay={20}
          className={classNames(
            'bg-white w-full flex flex-col rounded-tr-xl rounded-tl-xl md:!rounded-lg md:w-[28rem] max-h-screen overflow-auto overscroll-contain',
            {
              'h-full overflow-hidden rounded-tr-none rounded-tl-none': fullScreen,
              'overflow-visible': noScroll,
            },
            className,
          )}
          onClick={e => e.stopPropagation()}
        >
          {noHeader && !noLine && <div className="h-1 mx-auto mt-4 rounded-full w-11 md:hidden bg-slate-300" />}
          {!noHeader && (
            <div className="flex items-center justify-between p-4 border-b border-slate-100">
              <span className="font-bold line-clamp-1">{title}</span>
              <CloseIcon onClick={props.dismissible === false ? undefined : onClose} className="cursor-pointer" />
            </div>
          )}
          <div className={classNames('p-5 flex-grow overflow-auto no-scroll', bodyClassName, { 'overflow-visible': noScroll })}>
            {children}
          </div>
        </Transition>
      </Transition>
    </ClientOnlyPortal>
  );
};

/**
 * موبایل: همان باتم‌شیت داشبورد پزشک (`DsDrawer`) با Back مرورگر و مسیر در آدرس.
 * دسکتاپ: همان مودال قبلی، بدون تغییر.
 */
export const Modal = (props: ModalProps) => {
  const { isMobile } = useResponsive();
  useSheetHistory(props.isOpen && isMobile && props.dismissible !== false && props.bindHistory !== false, props.onClose, props.name ?? 'modal');
  if (!isMobile) return <DesktopModal {...props} />;
  return <MobileModal {...props} />;
};

const MobileModal = (props: ModalProps) => {
  const { title, isOpen, onClose, children, fullScreen, bodyClassName, className, noHeader = false, noScroll = false } = props;

  return (
    <DsDrawer
      dismissible={props.dismissible}
      nested={props.nested}
      open={isOpen}
      onOpenChange={next => !next && onClose()}
      title={typeof title === 'string' ? title : undefined}
      level={MODAL_LEVEL}
      fullHeight={fullScreen}
      className={classNames('!p-0', { 'overflow-visible': noScroll }, className)}
      hasVisibleTitle={!noHeader}
    >
      {!noHeader && (
        <div className="flex items-center justify-between gap-3 px-5 pb-3 border-b border-slate-100">
          <Drawer.Title asChild>
            <div className="font-bold leading-8 line-clamp-1">{title}</div>
          </Drawer.Title>
          <button
            type="button"
            onClick={props.dismissible === false ? undefined : onClose}
            aria-label="بستن"
            className="-ml-1.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-slate-600 active:bg-slate-100"
          >
            <CloseIcon className="h-5 w-5" />
          </button>
        </div>
      )}
      <div
        className={classNames(
          'p-5 flex-grow no-scroll pb-[max(1.25rem,env(safe-area-inset-bottom))]',
          noScroll ? 'overflow-visible' : 'overflow-auto',
          bodyClassName,
        )}
      >
        {children}
      </div>
    </DsDrawer>
  );
};

export default Modal;
